import { useGetAccountsQuery, useGetPartnerQuery } from '@/api/api.slice';
import { CancelInvitationAlert } from '@/features/accounts/CancelInvitationAlert.tsx';
import { InviteNewUserDialog } from '@/features/accounts/InviteNewUserDialog.tsx';
import {
  Alert,
  Button,
  CircledQuestionMarkIcon,
  Pagination,
  Paper,
  Skeleton,
  TableOrCards,
  TextField,
  Tooltip,
  Typography,
  useSearchPaginationContext,
  useTableOrCardsDisplayMode,
} from '@arsnovasystems/ars-front-lib';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { useAppDispatch } from '@/app/hooks.ts';
import { setDialogInviteOpen } from '@/features/accounts/accounts.slice.ts';
import { ActionsCell } from '@/features/accounts/ActionsCell.tsx';
import { CustomerCard } from '@/features/accounts/CustomerCard.tsx';
import { AccountType } from '@/features/accounts/types.ts';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Stack } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Customer accounts screen
 *
 * @returns JSX of the component
 */
export const CustomerAccounts = () => {
  const { t } = useTranslation();
  const searchContext = useSearchPaginationContext();
  const { data: me } = useGetPartnerQuery();
  const dispatch = useAppDispatch();

  // eslint-disable-next-line i18next/no-literal-string
  const [displayMode, setDisplayMode] = useTableOrCardsDisplayMode('customers');

  const { data, isLoading, isFetching } = useGetAccountsQuery({
    paginate: searchContext.pagination,
    search: {
      searchFullText:
        searchContext.searchString !== '' ?
          searchContext.searchString
        : undefined,
    },
  });

  const cols = useMemo(() => {
    const columnHelper = createColumnHelper<AccountType>();

    return [
      columnHelper.accessor('companyName', {
        header: t('ik.customerAccounts.columnHeader.company'),
      }),
      columnHelper.accessor('contactName', {
        header: t('ik.customerAccounts.columnHeader.name'),
      }),
      columnHelper.accessor('email', {
        size: 250,
        header: t('ik.customerAccounts.columnHeader.email'),
      }),
      columnHelper.accessor('licencesCount', {
        header: t('ik.customerAccounts.columnHeader.licencesCount'),
      }),
      columnHelper.accessor('endSubscriptionDate', {
        size: 250,
        header: t('ik.customerAccounts.card.subscriptionEnd'),
      }),
      columnHelper.accessor('endOfTrialDate', {
        size: 250,
        header: t('ik.customerAccounts.card.trialEnd'),
      }),
      columnHelper.display({
        id: 'actions',
        size: 250,
        meta: {
          flex: 1,
        },
        header: t('ik.table.header.actions'),
        cell: (param) => {
          return <ActionsCell data={param.row.original} />;
        },
      }),
    ];
  }, [t]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      searchContext.setSearchQuery(event.target.value);
    },
    [searchContext],
  );

  return (
    <Fragment>
      <Paper
        sx={{
          paddingBottom: 10,
          padding: 4,
          minHeight: 'calc(100% - 300px)',
        }}
      >
        <Stack
          direction={'row'}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant={'h1'}
            sx={(theme) => ({
              color: theme.palette.neutral.dark,
            })}
          >
            {t('Customer accounts')}
          </Typography>

          <Button
            variant='contained'
            color={'secondary'}
            startIcon={<AddCircleOutlineIcon sx={{ color: 'white' }} />}
            onClick={() => {
              dispatch(setDialogInviteOpen(true));
            }}
            sx={{
              height: 'fit-content',
            }}
          >
            {t('ik.invite.a.new.user')}
          </Button>
        </Stack>

        {me ?
          <Alert
            sx={{
              marginTop: 4,
            }}
            severity={'info'}
          >
            <Stack
              direction={'row'}
              sx={{ gap: 1 }}
            >
              <Typography>
                {t('Manager email : {{ email }}', { email: me.email })}
              </Typography>
              <Tooltip
                title={t(
                  'Manager email associated with all your linked client accounts. To modify it, please log in to one of your client accounts and navigate to the "My Account" tab to update the manager email address.',
                )}
              >
                <div>
                  <CircledQuestionMarkIcon
                    sx={{
                      fontSize: '1.8rem',
                    }}
                  />
                </div>
              </Tooltip>
            </Stack>
          </Alert>
        : <Skeleton />}

        <Fragment>
          <TableOrCards<AccountType>
            displayMode={displayMode}
            onDisplayModeSwitch={(displayMode) => {
              setDisplayMode(displayMode);
            }}
            sx={{
              marginTop: 4,
            }}
            SearchComponent={
              <TextField
                label={t('Search')}
                onChange={handleInputChange}
                slotProps={{
                  input: {
                    endAdornment: (
                      <SearchRoundedIcon
                        sx={(theme) => ({
                          color: theme.palette.text.secondary,
                        })}
                      />
                    ),
                  },
                }}
                sx={{
                  minWidth: '30vw',
                }}
              />
            }
            columns={cols}
            loading={isLoading}
            fetching={isFetching}
            data={data?.data ?? []}
            renderCard={({ row, index }) => (
              <CustomerCard
                key={index}
                account={row}
              />
            )}
          />
          {data ?
            <Stack sx={{ alignItems: 'center' }}>
              <Pagination
                sx={{ mt: 6 }}
                count={data.pagination.total}
                rowsPerPage={data.pagination.limit}
                page={searchContext.pagination.page}
                onPageChange={(_, newPage) => {
                  searchContext.handlePagination({
                    page: newPage,
                  });
                }}
                onRowsPerPageChange={(e) => {
                  searchContext.handlePagination({
                    limit: parseInt(e.target.value),
                  });
                }}
              />
            </Stack>
          : null}
        </Fragment>
      </Paper>
      <InviteNewUserDialog />
      <CancelInvitationAlert />
    </Fragment>
  );
};
